import { Dialog, Transition } from "@headlessui/react";
import { Fragment, useRef } from "react";
import Input from "@/app/components/ui/Inputs/Input";
import { useForm } from "react-hook-form";
import useFormValidations from "@/app/hooks/useFormValidations";
import { useRouter } from "@/app/hooks/useRouter";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import { ICustomerProfileState } from "@/app_redux/reducers/slices/auth/customer_profile_slice";
import { FiCopy, FiX } from "react-icons/fi";
import clsx from "clsx";
import { toast } from "react-hot-toast";
import { useProceedPaymentAPI } from "@/app/hooks/api_hooks/HDCart/useProceedPaymentAPI";
import OrderPlacedModal from "../OrderPlacedModal";

export interface ICheckoutProps {
  phone_number: string;
}

export default function HDCartCheckoutModal({
  open,
  setOpen,
  orderResponse,
  cod,
  orderPlacedOpen,
  setOrderPlacedOpen,
  phoneNumber,
}) {
  const cancelButtonRef = useRef(null);
  const { forOnlyNumber } = useFormValidations();
  const { getRouteKey } = useRouter();
  const { routeState } = useRouteInfo(getRouteKey("CUSTOMER_PROFILE", "id"));
  const fetchProceedPayment = useProceedPaymentAPI();
  const prefillMobile = phoneNumber;

  const formHook = useForm<ICheckoutProps>({
    mode: "onChange",
    defaultValues: {
      phone_number: prefillMobile,
    },
    shouldFocusError: true,
    criteriaMode: "all",
  });

  const handleCopyLink = async () => {
    if (orderResponse?.payment_link_details?.payment_link_url) {
      await navigator.clipboard.writeText(orderResponse?.payment_link_details?.payment_link_url);
      toast.success("Payment link copied!");
    }
  };

  const handleProceed = async () => {
    if (!formHook.formState.isValid) return;
    try {
      const res = await fetchProceedPayment(
        formHook.getValues("phone_number"),
        orderResponse?.payment_link_details?.payment_link_url,
      );
      if (res?.status) {
        toast.success("Payment link processed successfully!");
        setOpen(false);
        sessionStorage.setItem("checkoutModalOpen", "false");
        sessionStorage.removeItem("pendingOrderId");
        sessionStorage.removeItem("pendingOrderData");
        setOrderPlacedOpen(true);
      } else {
        toast.error("Failed to process payment.");
      }
    } catch (error) {
      toast.error("Failed to process payment. Please try again.");
    }
  };

  return (
    <div>
      <Transition.Root show={open} as={Fragment}>
        <Dialog as="div" className="relative z-40" initialFocus={cancelButtonRef} onClose={() => setOpen(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>
          <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel
                  className={`relative transform rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-4 sm:w-[500px] sm:p-6 flex flex-col `}
                >
                  <button
                    type="button"
                    className="absolute right-4 top-4 text-gray-400 hover:text-gray-600 cursor-pointer"
                    onClick={() => setOpen(false)}
                  >
                    <FiX size={20} />
                  </button>
                  <div className="pb-2">
                    <div className="font-semibold text-sm">Send Payment Link</div>
                  </div>
                  <div>
                    <div className="flex justify-between items-center mt-3">
                      <div>
                        <Input
                          className="text-xs"
                          placeholder="Phone Number"
                          register={formHook.register("phone_number", {
                            required: false,
                            ...forOnlyNumber.validations,
                          })}
                          fieldError={formHook.formState.errors.phone_number}
                          errorMessages={[forOnlyNumber.errors, { message: null, type: "required" }]}
                          defaultValue={prefillMobile}
                        />
                        <div className="text-[#747984] text-xs mt-4">Update number if needed</div>
                      </div>
                      {orderResponse?.payment_link_details?.payment_link_url && (
                        <div className="cursor-pointer flex gap-3 items-centers" onClick={handleCopyLink}>
                          <div className="text-[#0753ec] font-medium text-sm">Copy payment link</div>
                          <div className="text-[#4157ec]">
                            <FiCopy size={20} />
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="mt-3 bg-[#ffe7cf] p-2 rounded-md text-sm">
                      Invoices will be cancelled, if payment not received in 2 hours
                    </div>
                    <div className="mt-5">
                      <button
                        type="submit"
                        onClick={handleProceed}
                        disabled={!formHook.formState.isValid}
                        className={clsx(
                          "rounded-md w-full px-10 py-[9px] font-normal text-sm shadow-sm",
                          formHook.formState.isValid
                            ? "bg-black text-white cursor-pointer"
                            : "bg-gray-300 text-gray-500 cursor-not-allowed",
                        )}
                      >
                        Proceed
                      </button>
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
      <OrderPlacedModal open={orderPlacedOpen} cod={cod} />
    </div>
  );
}
